import React, { ReactNode } from 'react';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import Snackbar, { SnackbarOrigin } from '@mui/material/Snackbar';
import { ThemeProvider, Backdrop, Portal } from '@mui/material';
import { createTheme } from '@mui/material';
import { colors } from 'src/sass/common/_variables';
import { ReactComponent as SuccessIcon } from 'src/assets/icons/toast-success-v2.svg';
import { ReactComponent as InfoIcon } from 'src/assets/icons/toast-info-v2.svg';
import { ReactComponent as ErrorIcon } from 'src/assets/icons/toast-error-v2.svg';
import { ReactComponent as WarningIcon } from 'src/assets/icons/toast-warning-v2.svg';
import ButtonV2 from '../ButtonV2';
import { ActionConfig } from '../ModalV2';

interface IPropsType {
  message?: string | ReactNode;
  heading: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  type: 'error' | 'warning' | 'info' | 'success' | 'neutral';
  closeActionConfig?: ActionConfig;
  submitActionConfig?: ActionConfig;
  isBlocking?: boolean;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export const Toaster = (props: IPropsType) => {
  const { isBlocking = false } = props;

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    props.setOpen(false);
  };

  const getAlertIcon = (type: IPropsType['type']) => {
    switch (type) {
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      case 'success':
        return <SuccessIcon />;
      default:
        return false;
    }
  };

  // Determine anchor position based on blocking mode
  const getAnchorOrigin = (): SnackbarOrigin => {
    if (isBlocking || props.type === 'neutral') {
      return { vertical: 'top', horizontal: 'center' };
    }
    return { vertical: 'bottom', horizontal: 'left' };
  };

  // Get custom styles for Snackbar positioning
  const getSnackbarStyles = () => {
    if (props.type === 'neutral') {
      // Center both horizontally and vertically for neutral type
      return {
        position: 'fixed' as const,
        top: '40% !important',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        margin: 0,
      };
    }
    return undefined;
  };

  return (
    <>
      {/* Backdrop for blocking mode */}
      {isBlocking && (
        <Backdrop
          open={props.open}
          onClick={handleClose}
          sx={{
            zIndex: (theme) => theme.zIndex.snackbar - 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        />
      )}

      <ThemeProvider theme={props.type === 'neutral' ? neutralTheme : customTheme}>
        {/*
          Portal to document.body so the fixed-position Snackbar escapes any
          ancestor that establishes a containing block (transform/filter/
          will-change) and clips it — e.g. on the settings/manage-users page,
          where the toast was otherwise cut off by an overflow:hidden ancestor.
        */}
        <Portal>
          <Snackbar
            open={props.open}
            autoHideDuration={isBlocking || props.type === 'neutral' ? null : 4000}
            onClose={handleClose}
            anchorOrigin={getAnchorOrigin()}
            disableWindowBlurListener
            sx={getSnackbarStyles()}
          >
          <Alert
            severity={props.type === 'neutral' ? 'info' : props.type}
            icon={getAlertIcon(props.type)}
            onClose={() => props.setOpen(false)}
            variant="standard"
            sx={{
              ...(props.type === 'neutral' ? { bgcolor: 'background.paper' } : {}),
              '& .MuiAlert-action': {
                marginLeft: '0px',
                paddingLeft: '1px',
                '& .MuiIconButton-root': {
                  color: colors.$colorWhite,
                },
              },
            }}
          >
            <div className="toaster-wrapper">
              <div
                className="toaster-body"
                style={{
                  minWidth: props.closeActionConfig ? '150px' : '220px',
                }}
              >
                <div
                  className="toaster-heading"
                  style={
                    props.message
                      ? {}
                      : {
                          marginTop: '1px',
                        }
                  }
                >
                  {props.heading}
                </div>
                {props.message && (
                  <div
                    className="toaster-content-message"
                    style={
                      props.heading
                        ? {}
                        : {
                            marginTop: '-6px',
                          }
                    }
                  >
                    {props.message}
                  </div>
                )}
              </div>
              <div className="toaster-actions">
                {/* {props.submitActionConfig && (
                  <ButtonV2
                    type="primary"
                    variant={props.submitActionConfig.variant || 'contained'}
                    onClick={props.submitActionConfig.handleAction}
                    startIcon={props.submitActionConfig.icon}
                    text={props.submitActionConfig.label}
                    loading={props.submitActionConfig.loading}
                    disabled={props.submitActionConfig.disabled}
                    id={props.submitActionConfig.id}
                  />
                )} */}
                {props.closeActionConfig && (
                  <div className="toaster-close-btn-div">
                    <ButtonV2
                      type="inherited"
                      variant={props.closeActionConfig.variant || 'text'}
                      onClick={props.closeActionConfig.handleAction}
                      startIcon={props.closeActionConfig.icon}
                      text={props.closeActionConfig.label}
                      loading={props.closeActionConfig.loading}
                      disabled={props.closeActionConfig.disabled}
                      id={props.closeActionConfig.id}
                      className="toaster-close-btn"
                      size="small"
                    />
                  </div>
                )}
              </div>
            </div>
          </Alert>
        </Snackbar>
        </Portal>
      </ThemeProvider>
    </>
  );
};

const customTheme = createTheme({
  components: {
    MuiSnackbar: {
      styleOverrides: {
        root: {
          marginLeft: '60px',
          marginBottom: '6px',
          width: '320px',
          borderRadius: '12px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          height: '100%',
          width: '320px',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: `${colors.$colorPrimary500}`,
          boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.10)',
        },
      },
      variants: [
        {
          props: { severity: 'error' },
          style: {
            background: `linear-gradient(0deg, ${colors.$tooltipBgColor} 0%, #565656 87.72%)`,
          },
        },
        {
          props: { severity: 'warning' },
          style: {
            background: `linear-gradient(0deg, ${colors.$tooltipBgColor} 0%, #565656 87.72%)`,
          },
        },
        {
          props: { severity: 'info' },
          style: {
            background: `linear-gradient(0deg, ${colors.$tooltipBgColor} 0%, #565656 87.72%)`,
          },
        },
        {
          props: { severity: 'success' },
          style: {
            background: `linear-gradient(0deg, ${colors.$tooltipBgColor} 0%, #565656 87.72%)`,
          },
        },
      ],
    },
  },
});

const neutralTheme = createTheme({
  components: {
    MuiSnackbar: {
      styleOverrides: {
        root: {
          marginLeft: '0px',
          width: '320px',
          borderRadius: '12px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          height: '100%',
          width: '320px',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: `${colors.$colorPrimary500}`,
          boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.10)',
        },
      },
      variants: [
        {
          props: { severity: 'info' },
          style: {
            background: `linear-gradient(0deg, ${colors.$tooltipBgColor} 0%, #565656 87.72%)`,
          },
        },
      ],
    },
  },
});
